import type { BillingPeriod } from "@iec-id/shared/contracts/billing";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CheckoutStep } from "@/components/plus/checkout-step";
import { PLUS_NS } from "@/components/plus/i18n";
import { PlansGrid } from "@/components/plus/plans-grid";
import { ServiceEntitlements } from "@/components/plus/service-entitlements";
import { SubscriptionCard } from "@/components/plus/subscription-card";

/**
 * Шаг оплаты живёт в адресной строке:
 * `?checkout=plus|pro[&service=<id>][&period=monthly|yearly][&return=<url>]`.
 * Так «Назад» браузера возвращает к витрине, а обновление страницы не теряет
 * выбранный тариф.
 *
 * `period` и `return` появились ради ВНЕШНЕЙ ссылки: сервис экосистемы уводит
 * человека прямо на оплату своего тарифа и ждёт его обратно у себя
 * (INTEGRATION-GUIDE §4.5). До этого адрес возврата витрина ставила себе сама,
 * и человек после оплаты оставался у нас — сервис узнавал о покупке только
 * SET-событием и не мог показать «спасибо за покупку» вовсе.
 *
 * Изнутри витрины оба параметра не ставятся: период выбирается на витрине
 * планов и живёт в состоянии, а возвращать человека некуда — он уже здесь.
 */
const PlusSearchSchema = z.object({
	checkout: z.enum(["plus", "pro"]).optional(),
	service: z.string().min(2).max(64).optional(),
	period: z.enum(["monthly", "yearly"]).optional(),
	/**
	 * Адрес возврата сервиса. Проверяется по реестру экосистемы уже на месте
	 * применения (`resolveGrantReturnUrl`), а не здесь: схема роутера решает,
	 * что параметр вообще есть, а не кому его можно доверить.
	 */
	return: z.string().min(8).max(2000).optional(),
});

export const Route = createFileRoute("/plus")({
	component: PlusSection,
	validateSearch: PlusSearchSchema,
});

/** Черновик шага оплаты: тариф выбран, checkout ещё не стартовал. */
interface CheckoutDraft {
	tier: "plus" | "pro";
	serviceId?: string;
}

/**
 * Раздел «IEC+» (макет 3b, мобильная версия — 3e, состояние «Платёж не прошёл» — 3f).
 * Одна колонка на всю ширину: правой рейки у этого раздела в макете нет.
 *
 * Два шага на одном роуте: витрина тарифов и оплата выбранного, переключение —
 * search-параметром `checkout`.
 */
function PlusSection() {
	const { t } = useTranslation(PLUS_NS);
	const search = Route.useSearch();
	const navigate = useNavigate();
	// Период из адреса — начальное значение, дальше им управляет витрина планов.
	const [period, setPeriod] = useState<BillingPeriod>(search.period ?? "monthly");

	const startCheckout = (draft: CheckoutDraft) =>
		void navigate({
			to: "/plus",
			search: { checkout: draft.tier, ...(draft.serviceId ? { service: draft.serviceId } : {}) },
		});
	const backToPlans = () => void navigate({ to: "/plus", search: {} });

	return (
		<div className="flex flex-col gap-1">
			{/* Заголовка-карточки у раздела в макете 3b нет: первой идёт карточка
			    статуса. Имя раздела остаётся для скринридера и оглавления. */}
			<h1 className="sr-only">{t("title")}</h1>
			{search.checkout ? (
				<CheckoutStep
					tier={search.checkout}
					period={period}
					serviceId={search.service}
					returnTo={search.return}
					onBack={backToPlans}
				/>
			) : (
				<>
					<SubscriptionCard />
					{/* Права по каждому сервису отдельно: подарки Д7 выдаются per-service,
					    и карточка статуса выше показывает только максимум по экосистеме. */}
					<ServiceEntitlements />
					<PlansGrid period={period} onPeriodChange={setPeriod} onStartCheckout={startCheckout} />
				</>
			)}
		</div>
	);
}
